import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Copy, Calendar, ChevronDown, Sun, Sunset, Clock4, Coffee, Check } from 'lucide-react';
import TimePicker24h from '../ui/TimePicker24h';

const WEEKDAYS = [
  { id: 'monday', label: 'Lundi' },
  { id: 'tuesday', label: 'Mardi' },
  { id: 'wednesday', label: 'Mercredi' },
  { id: 'thursday', label: 'Jeudi' },
  { id: 'friday', label: 'Vendredi' },
];

const WEEKEND = [
  { id: 'saturday', label: 'Samedi' },
  { id: 'sunday', label: 'Dimanche' },
];

const TRIP_TYPES = [
  { id: 'morning', label: 'Matin (Ramassage)' },
  { id: 'noon', label: 'Midi (Dépose/Ramassage)' },
  { id: 'afternoon', label: 'Après-midi (Dépose)' },
  { id: 'evening', label: 'Soir (Dépose)' },
];

const TYPE_CONFIG = {
  morning: {
    icon: Sun,
    dot: 'bg-sky-400',
    slot: 'bg-sky-50 border-sky-200',
    select: 'bg-sky-50 text-sky-800 border-sky-200 focus:ring-sky-300',
    label: 'text-sky-700',
  },
  noon: {
    icon: Coffee,
    dot: 'bg-emerald-400',
    slot: 'bg-emerald-50 border-emerald-200',
    select: 'bg-emerald-50 text-emerald-800 border-emerald-200 focus:ring-emerald-300',
    label: 'text-emerald-700',
  },
  afternoon: {
    icon: Clock4,
    dot: 'bg-violet-400',
    slot: 'bg-violet-50 border-violet-200',
    select: 'bg-violet-50 text-violet-800 border-violet-200 focus:ring-violet-300',
    label: 'text-violet-700',
  },
  evening: {
    icon: Sunset,
    dot: 'bg-amber-400',
    slot: 'bg-amber-50 border-amber-200',
    select: 'bg-amber-50 text-amber-800 border-amber-200 focus:ring-amber-300',
    label: 'text-amber-700',
  },
};

// ─── Custom portal-based type selector — avoids native <select> dropdown clipping ───
const TripTypeSelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const cfg = TYPE_CONFIG[value] || TYPE_CONFIG.morning;
  const currentLabel = TRIP_TYPES.find((t) => t.id === value)?.label ?? '';

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownH = TRIP_TYPES.length * 44 + 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < dropdownH && rect.top > dropdownH;
    setPos({
      top: openAbove ? rect.top + window.scrollY - dropdownH - 4 : rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, []);

  const handleOpen = () => {
    calcPos();
    setIsOpen((v) => !v);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target) || dropdownRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    const onScroll = () => calcPos();
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isOpen, calcPos]);

  const handleSelect = (id) => {
    onChange(id);
    setIsOpen(false);
  };

  const dropdown =
    isOpen &&
    createPortal(
      <div
        ref={dropdownRef}
        style={{ position: 'absolute', top: pos.top, left: pos.left, width: Math.max(pos.width, 200), zIndex: 9999 }}
        className="bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden py-1"
      >
        {TRIP_TYPES.map((t) => {
          const optCfg = TYPE_CONFIG[t.id] || TYPE_CONFIG.morning;
          const Icon = optCfg.icon;
          const isSelected = t.id === value;
          return (
            <button
              key={t.id}
              onPointerDown={(e) => {
                e.preventDefault();
                handleSelect(t.id);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'
              }`}
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${optCfg.dot}`} />
              <Icon className={`w-3.5 h-3.5 shrink-0 ${optCfg.label}`} />
              <span className={`text-xs font-semibold flex-1 ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                {t.label}
              </span>
              {isSelected && <Check className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
            </button>
          );
        })}
      </div>,
      document.body,
    );

  return (
    <div className="w-full min-w-0" ref={triggerRef}>
      <button
        onPointerDown={handleOpen}
        className={`w-full flex items-start gap-1.5 px-1.5 py-1.5 rounded-md border text-xs font-semibold cursor-pointer transition-all text-left ${cfg.select} ${
          isOpen ? 'ring-2' : ''
        }`}
      >
        <span className={`flex-1 min-w-0 line-clamp-2 ${cfg.label}`}>{currentLabel}</span>
        <ChevronDown
          className={`w-3 h-3 shrink-0 mt-[1px] transition-transform duration-150 ${cfg.label} ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {dropdown}
    </div>
  );
};

const WeeklyScheduleEditor = ({ initialSchedule, onChange }) => {
  const [schedule, setSchedule] = useState(
    initialSchedule || {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    },
  );

  const hasWeekendSlots = (s) => (s.saturday?.length || 0) + (s.sunday?.length || 0) > 0;

  const [showWeekend, setShowWeekend] = useState(() => hasWeekendSlots(initialSchedule || {}));

  useEffect(() => {
    onChange(schedule);
  }, [schedule, onChange]);

  const addSlot = (dayId) => {
    setSchedule((prev) => ({
      ...prev,
      [dayId]: [...prev[dayId], { type: 'morning', start: '07:00', end: '08:00' }],
    }));
  };

  const removeSlot = (dayId, index) => {
    setSchedule((prev) => ({
      ...prev,
      [dayId]: prev[dayId].filter((_, i) => i !== index),
    }));
  };

  const updateSlot = (dayId, index, field, value) => {
    setSchedule((prev) => {
      const slots = [...prev[dayId]];
      slots[index] = { ...slots[index], [field]: value };
      return { ...prev, [dayId]: slots };
    });
  };

  const copyDayToAll = (sourceDayId) => {
    const sourceLabel = WEEKDAYS.find((d) => d.id === sourceDayId)?.label || sourceDayId;
    if (!window.confirm(`Copier les horaires de ${sourceLabel} vers tous les autres jours (Lun–Ven) ?`)) return;
    const sourceSlots = schedule[sourceDayId];
    setSchedule((prev) => {
      const next = { ...prev };
      WEEKDAYS.forEach(({ id }) => {
        if (id !== sourceDayId) next[id] = JSON.parse(JSON.stringify(sourceSlots));
      });
      return next;
    });
  };

  const renderDay = (day) => {
    const slots = schedule[day.id] || [];
    return (
      <motion.div
        key={day.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-gray-200 rounded-xl shadow-sm bg-white"
      >
        {/* Card header */}
        <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-t-xl border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-bold text-gray-900 truncate">{day.label}</span>
            {slots.length > 0 && (
              <span className="text-xs text-gray-500 bg-white border border-gray-200 px-1.5 py-0.5 rounded-full leading-none shrink-0 tabular-nums">
                {slots.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              onClick={() => copyDayToAll(day.id)}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Copier vers Lun–Ven"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => addSlot(day.id)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Ajouter un créneau"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Slots list */}
        <div className="p-2.5 space-y-2">
          {slots.length === 0 ? (
            <button
              onClick={() => addSlot(day.id)}
              className="w-full py-5 text-xs text-gray-400 rounded-lg border border-dashed border-gray-200 hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50/40 transition-all"
            >
              + Ajouter un créneau
            </button>
          ) : (
            <AnimatePresence initial={false}>
              {slots.map((slot, index) => {
                const cfg = TYPE_CONFIG[slot.type] || TYPE_CONFIG.morning;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.1 } }}
                    transition={{ duration: 0.15 }}
                    className={`rounded-lg border p-2 space-y-1.5 ${cfg.slot}`}
                  >
                    {/* Type selector row */}
                    <TripTypeSelect value={slot.type} onChange={(v) => updateSlot(day.id, index, 'type', v)} />

                    {/* Start time */}
                    <div className="space-y-0.5">
                      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide pl-0.5">
                        Début
                      </span>
                      <TimePicker24h
                        value={slot.start}
                        onChange={(v) => updateSlot(day.id, index, 'start', v)}
                        className="w-full"
                      />
                    </div>

                    {/* End time + delete */}
                    <div className="space-y-1.5">
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide pl-0.5">
                          Fin
                        </span>
                        <TimePicker24h
                          value={slot.end}
                          onChange={(v) => updateSlot(day.id, index, 'end', v)}
                          className="w-full"
                        />
                      </div>
                      <button
                        onClick={() => removeSlot(day.id, index)}
                        className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-100 transition-colors"
                        title="Supprimer ce créneau"
                      >
                        <Trash2 className="w-3 h-3" />
                        Supprimer
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3">
        <Calendar className="w-4 h-4 text-blue-600 shrink-0" />
        <p className="text-sm text-blue-700">
          Définissez les horaires standards des bus — appliqués par défaut à tous les nouveaux circuits.
        </p>
      </div>

      {/* Weekdays grid */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Semaine</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">{WEEKDAYS.map(renderDay)}</div>
      </div>

      {/* Weekend toggle */}
      <button
        onClick={() => setShowWeekend((v) => !v)}
        className="flex items-center justify-center gap-2 w-full py-2.5 text-sm text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 hover:border-gray-400 rounded-xl transition-colors"
      >
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showWeekend ? 'rotate-180' : ''}`} />
        {showWeekend ? 'Masquer le week-end' : 'Afficher le week-end (Sam · Dim)'}
        {!showWeekend && hasWeekendSlots(schedule) && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
            {(schedule.saturday?.length || 0) + (schedule.sunday?.length || 0)} créneau(x)
          </span>
        )}
      </button>

      {/* Weekend grid */}
      <AnimatePresence>
        {showWeekend && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Week-end</p>
            <div className="grid grid-cols-2 gap-3">{WEEKEND.map(renderDay)}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WeeklyScheduleEditor;
